import { Loader, TextInput } from "@mantine/core";
import { useRequest } from "ahooks";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdSearch } from "react-icons/md";
import { getManufacturers, getOrdersData } from "../../api";
import { useUser } from "../../contexts";
import { Manufacturer, SortBy, StatusConst, UserOrder } from "../../interfaces";
import AccountLayout from "../../layouts/AccountLayout";
import OrderTableInfiniteScroll from "./OrderTableInfiniteScroll";
import DropDown from "../common/DropDown";
import { ALL } from "../../utils/constants";
import { ModelOrdersFilterInput } from "../../interfaces";
import useOrderColumn, { STATUS_OPTIONS } from "../../hooks/useOrderColumn";
import ErrorBoundary from "../ErrorBoundary";
import {
  ADMIN_DASHBOARD_COLUMNS,
  DASHBOARD_COLUMNS,
} from "../../constant/DashboardConstant";
import useSubscriber from "../../hooks/useSubscriber";
import {
  onCreateOrderItem,
  onUpdateOrderItem,
// @ts-expect-error type exists
} from "../../graphql/subscriptions";
// @ts-expect-error type exists
import { customOnDeleteOrderItem } from "../../graphql/customSubscriptions";

export type TOption = {
  key: string;
  value: string;
};
export enum EFilterBy {
  byDate = "byDate",
  byOrderId = "byOrderId",
  byStatus = "byStatus",
  byPrintManufacturer = "byPrintManufacturer",
  byDeliveryDate = "byDeliveryDate",
  byUploadedDate = "byUploadedDate",
  byUploadedAndReuploaded = "byUploadedAndReuploaded",
}
interface StatusOption {
  key: string;
  value: string;
  color?: string;
}

export type TFilter = {
  nextToken: string | null;
  limit: number;
  orderNumber: string | null;
  sortByOrder: SortBy | null;
  filterBy: EFilterBy;
  filter: ModelOrdersFilterInput;
};
const LIMIT = 16;
const UploadedAndReuploaded: StatusOption = {
  key: "byUploadedAndReuploaded",
  value: "UploadedAndReuploaded",
  color: "",
};

const FILTER_STATUS_OPTIONS = [{ key: ALL, value: ALL }, ...STATUS_OPTIONS];
FILTER_STATUS_OPTIONS.push(
  UploadedAndReuploaded as unknown as {
    key: StatusConst;
    value: string;
    color: string;
  }
);
const Dashboard: React.FC = () => {
  const [{ user }] = useUser();
  const [orders, setOrders] = useState<UserOrder[] | null>(null);
  const { data: updatedOrder } = useSubscriber(onUpdateOrderItem);
  const { data: createdOrder } = useSubscriber(onCreateOrderItem);
  const { data: deletedOrder } = useSubscriber(customOnDeleteOrderItem);
  const [orderFilter, setOrderFilter] = useState<TFilter>({
    nextToken: null,
    limit: LIMIT,
    orderNumber: null,
    sortByOrder: null,
    filterBy: EFilterBy.byDate,
    filter:
      user && user.name
        ? {
            printManufacturer: { eq: user.name },
          }
        : {},
  });
  const { t } = useTranslation();
  type OrdersProps = {
    items: UserOrder[];
    nextToken?: string;
  };

  const { data, loading, run } = useRequest<OrdersProps>((filter: TFilter) =>
    getOrdersData(filter)
  );
  const { data: manufacturers, loading: manufacturersLoading } = useRequest<
    Manufacturer[]
  >(() => getManufacturers());

  const { filterColumns } = useOrderColumn({
    orders,
    setOrders,
    setOrderFilter,
    orderFilter,
    manufacturers: manufacturers ?? [],
  });

  const updateOrderData = (data: OrdersProps | undefined) => {
    if (data?.items) {
      const updatedOrders = [...(orders || []), ...data?.items];
      setOrders(updatedOrders);
      // Only fetch more if we received items AND haven't reached limit AND have more pages
      if (data.items.length > 0 && updatedOrders.length !== orderFilter.limit && data?.nextToken) {
        orderFilter.limit = orderFilter.limit - data.items.length;
        orderFilter.nextToken = data.nextToken;
        if (orderFilter.limit) {
          run(orderFilter);
        } else {
          setOrderFilter({
            ...orderFilter,
            limit: orderFilter.limit < LIMIT ? LIMIT : orderFilter.limit,
            nextToken: data.nextToken || null,
          });
        }
      } else {
        setOrderFilter({
          ...orderFilter,
          limit: orderFilter.limit < LIMIT ? LIMIT : orderFilter.limit,
          nextToken: data.nextToken || null,
        });
      }
    }
  };

  useEffect(() => {
    if (!loading) {
      updateOrderData(data);
    }


  }, [loading, data]);

  useEffect(() => {
  const fetchData = async () => {
    if (orderFilter.filterBy === EFilterBy.byUploadedAndReuploaded) {
      const [uploadedRes, reuploadedRes] = await Promise.all([
        getOrdersData({ 
          ...orderFilter,
          filterBy: EFilterBy.byStatus,
          filter: {
            ...orderFilter.filter,
            status: { eq: StatusConst.Uploaded },
          },
          nextToken: null,
        }),
        getOrdersData({
          ...orderFilter,
          filterBy: EFilterBy.byStatus,
          filter: {
            ...orderFilter.filter,
            status: { eq: StatusConst.ReUploaded },
          },
          nextToken: null,
        }),
      ]);

      const mergedItems = [
        ...(uploadedRes?.items ?? []),
        ...(reuploadedRes?.items ?? []),
      ].filter(
        (item, index, self) =>
          index === self.findIndex(o => o.id === item.id)
      );

      setOrders(mergedItems);
      return;
    }
    if (
      orderFilter.sortByOrder === null &&
      orderFilter.filterBy !== EFilterBy.byStatus
    ) {
      orderFilter.filterBy = EFilterBy.byDate;
    }

    run(orderFilter);
  };
  fetchData();
}, [
  orderFilter.sortByOrder,
  orderFilter.orderNumber,
  orderFilter.filter,
  orderFilter.filter.status,
  orderFilter.filterBy,
]);


  /**
   * This method is used to update order state locally
   * @param { Partial<UserOrder> & { id: string } } order order details that need to be updated in local state
   * @returns void
   */
  const updateOrderState = (order: Partial<UserOrder> & { id: string }) => {
    if (!orders) return;
    setOrders(
      orders.map((_order) => {
        if (_order.id === order.id) return { ..._order, ...order };
        return _order;
      })
    );
  };

  /**
   * This method is used to add new order state locally
   * @param { UserOrder } order order details that need to be added in local state
   * @returns void
   */
  const addNewOrderState = (order: UserOrder) => {
    if (!orders) return;
    setOrders([order, ...orders]);
  };

  /**
   * This method is used to remove order from state locally
   * @param { UserOrder } order order details that need to be remove in local state
   * @returns void
   */
  const removeOrderState = (order: UserOrder) => {
    if (!orders) return;
    setOrders(
      orders.filter((_order) => {
        if (_order.id === order.id) return false;
        return true;
      })
    );
  };

  useEffect(() => {
    if (
      !updatedOrder ||
      (updatedOrder &&
        !(updatedOrder as Record<"onUpdateOrderItem", UserOrder>)
          .onUpdateOrderItem)
    )
      return;
    updateOrderState(
      (updatedOrder as Record<"onUpdateOrderItem", UserOrder>).onUpdateOrderItem
    );
  }, [updatedOrder]);

  useEffect(() => {
    if (
      !createdOrder ||
      (createdOrder &&
        !(createdOrder as Record<"onCreateOrderItem", UserOrder>)
          .onCreateOrderItem)
    )
      return;
    addNewOrderState(
      (createdOrder as Record<"onCreateOrderItem", UserOrder>).onCreateOrderItem
    );
  }, [createdOrder]);

  useEffect(() => {
    if (
      !deletedOrder ||
      (deletedOrder &&
        !(deletedOrder as Record<"onDeleteOrderItem", UserOrder>)
          .onDeleteOrderItem)
    )
      return;
    removeOrderState(
      (deletedOrder as Record<"onDeleteOrderItem", UserOrder>).onDeleteOrderItem
    );
  }, [deletedOrder]);

  const loadOrders = async () => {
    setOrderFilter({ ...orderFilter, limit: orderFilter.limit + LIMIT });
    await run(orderFilter);
  };

  const handleSearch = (e: any) => {
    e.preventDefault();
    const orderNumber: string = e.target["orderNumber"].value;
    setOrders([]);
    if (orderFilter.filter.status) delete orderFilter.filter.status;
    setOrderFilter({
      ...orderFilter,
      filterBy: EFilterBy.byDate,
      limit: LIMIT,
      orderNumber: orderNumber || null,
      sortByOrder: null,
      nextToken: null,
    });
  };

  return (
    <AccountLayout account>
      <div className="table-filter">
        <form onSubmit={handleSearch}>
          <div className="form__row search-input">
            <TextInput
              icon={<MdSearch size={20} />}
              name="orderNumber"
              type="text"
              placeholder={t("orderNumber")}
            />
          </div>
        </form>
        <DropDown
          label={"byStatus"}
          disable={Boolean(orderFilter.orderNumber)}
          options={FILTER_STATUS_OPTIONS}
          value={
            FILTER_STATUS_OPTIONS.find(
              (status) =>
                orderFilter.filter.status &&
                orderFilter.filter.status?.eq === status.key
            )?.value ?? ALL
          }
          onSelect={(val: string) => {
            setOrders([]);
            if (val === ALL) {
              delete orderFilter.filter.status;
              setOrderFilter({
                ...orderFilter,
                filterBy: EFilterBy.byDate,
                nextToken: null,
              });
            } else if (val === "byUploadedAndReuploaded") {
              setOrders([]);
              setOrderFilter({
                ...orderFilter,
                filterBy: EFilterBy.byUploadedAndReuploaded,
                nextToken: null,
              });
            } else {
              delete orderFilter.filter.or;
              setOrderFilter({
                ...orderFilter,
                filterBy: EFilterBy.byStatus,
                filter: { ...orderFilter.filter, status: { eq: val } },
                nextToken: null,
              });
            }
          }}
        />
      </div>
      {!manufacturersLoading && manufacturers ? (
        <>
          {orders?.length === 0 && !loading ? (
            <div className="row row-center mt-30">
              <p>{t("noOrders")}</p>
            </div>
          ) : (
            <ErrorBoundary>
            <OrderTableInfiniteScroll
              columns={filterColumns(
                user && user.name ? DASHBOARD_COLUMNS : ADMIN_DASHBOARD_COLUMNS
              )}
              data={orders}
              nextToken={orderFilter.nextToken}
              loadOrders={loadOrders}
              fetchLoading={loading}
              filter={orderFilter}
              setOrderFilter={setOrderFilter}
            />
            </ErrorBoundary>
          )}
        </>
      ) : (
        <div className="row row-center mt-30">
          <Loader />
        </div>
      )}
    </AccountLayout>
  );
};

export default Dashboard;
